/**
 * Генераторы тестовых данных для PlaybookComponent
 *
 * Предоставляет готовые наборы данных для тестирования различных компонентов
 */

export interface PersonTestData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  website: string;
  notes: string;
}

export interface ProjectTestData {
  name: string;
  description: string;
  status: string;
  priority: string;
  startDate: string;
  endDate: string;
  budget: number;
  team: string[];
}

export interface FormValidationTestData {
  validData: Record<string, any>;
  invalidData: Record<string, any>;
  edgeCases: Record<string, any>;
}

/**
 * Генератор данных персон
 */
export const generatePersonData = (): PersonTestData => {
  const firstNames = ['Александр', 'Мария', 'Дмитрий', 'Анна', 'Михаил', 'Елена', 'Сергей', 'Ольга'];
  const lastNames = ['Иванов', 'Петрова', 'Сидоров', 'Козлова', 'Новиков', 'Морозова', 'Волков', 'Соколова'];
  const companies = ['ТехноСофт', 'ИнноГрупп', 'ДигиталПро', 'СистемПлюс', 'КреативЛаб'];
  const positions = ['Разработчик', 'Дизайнер', 'Менеджер', 'Аналитик', 'Архитектор'];

  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const company = companies[Math.floor(Math.random() * companies.length)];
  const position = positions[Math.floor(Math.random() * positions.length)];

  return {
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    phone: `+7 (${String(Math.floor(Math.random() * 900) + 100)}) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 90) + 10)}-${String(Math.floor(Math.random() * 90) + 10)}`,
    company,
    position,
    website: `https://${company.toLowerCase()}.ru`,
    notes: `Контакт из компании ${company}, работает в должности ${position.toLowerCase()}`
  };
};

/**
 * Генератор данных проектов
 */
export const generateProjectData = (): ProjectTestData => {
  const projectNames = [
    'Система управления контентом',
    'Мобильное приложение для продаж',
    'Интеграция с внешними API',
    'Автоматизация бизнес-процессов',
    'Платформа аналитики данных'
  ];

  const statuses = ['Планирование', 'В работе', 'Тестирование', 'Завершён', 'Приостановлен'];
  const priorities = ['Низкий', 'Средний', 'Высокий', 'Критический'];
  const teamMembers = ['Алексей И.', 'Мария П.', 'Сергей К.', 'Анна М.', 'Дмитрий Л.'];

  const name = projectNames[Math.floor(Math.random() * projectNames.length)];
  const status = statuses[Math.floor(Math.random() * statuses.length)];
  const priority = priorities[Math.floor(Math.random() * priorities.length)];

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - Math.floor(Math.random() * 90));

  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + Math.floor(Math.random() * 180) + 30);

  return {
    name,
    description: `Разработка и внедрение ${name.toLowerCase()}. Включает анализ требований, проектирование архитектуры, разработку и тестирование.`,
    status,
    priority,
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    budget: Math.floor(Math.random() * 2000000) + 500000,
    team: teamMembers.sort(() => 0.5 - Math.random()).slice(0, Math.floor(Math.random() * 3) + 2)
  };
};

/**
 * Генератор данных для тестирования валидации форм
 */
export const generateFormValidationData = (fieldType: string): FormValidationTestData => {
  const generators = {
    email: {
      valid: 'user@example.com',
      invalid: 'invalid-email',
      edge: 'test+tag@sub.domain.co.uk'
    },
    phone: {
      valid: '+7 (999) 123-45-67',
      invalid: '123abc',
      edge: '+1-800-555-0123'
    },
    name: {
      valid: 'Иван Петров',
      invalid: '123',
      edge: 'Анна-Мария де Ла Круз-Гонзалес'
    },
    number: {
      valid: 42,
      invalid: 'not-a-number',
      edge: -0.000001
    },
    url: {
      valid: 'https://example.com',
      invalid: 'not-a-url',
      edge: 'ftp://files.example.co.uk:8080/path?param=value#section'
    },
    date: {
      valid: '2024-01-15',
      invalid: 'invalid-date',
      edge: '2024-02-29' // Leap year edge case
    }
  };

  const data = generators[fieldType as keyof typeof generators] || generators.name;

  return {
    validData: { [fieldType]: data.valid },
    invalidData: { [fieldType]: data.invalid },
    edgeCases: { [fieldType]: data.edge }
  };
};

/**
 * Генератор больших наборов данных для таблиц
 */
export const generateTableData = (count: number = 50) => {
  const data: any[] = [];
  for (let i = 0; i < count; i++) {
    const person = generatePersonData();
    const project = generateProjectData();

    data.push({
      id: i + 1,
      name: `${person.firstName} ${person.lastName}`,
      email: person.email,
      company: person.company,
      position: person.position,
      project: project.name,
      status: project.status,
      priority: project.priority,
      budget: project.budget,
      createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
    });
  }
  return data;
};

/**
 * Генератор ошибок для тестирования
 */
export const generateTestErrors = () => {
  const errors = [
    { field: 'email', message: 'Неверный формат email адреса' },
    { field: 'phone', message: 'Номер телефона должен содержать 10 цифр' },
    { field: 'name', message: 'Имя не может быть пустым' },
    { field: 'password', message: 'Пароль должен содержать минимум 8 символов' },
    { field: 'confirmPassword', message: 'Пароли не совпадают' },
  ];

  return errors.reduce((acc, error) => {
    acc[error.field] = error.message;
    return acc;
  }, {} as Record<string, string>);
};

/**
 * Утилита для симуляции задержек (для тестирования loading состояний)
 */
export const simulateDelay = (ms: number = 1000): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Утилита для симуляции случайных ошибок
 */
export const simulateRandomError = (probability: number = 0.3): boolean => {
  return Math.random() < probability;
};

/**
 * Предопределенные наборы тестовых данных
 */
export const TestDataPresets = {
  /** Минимальные валидные данные */
  minimal: {
    name: 'Тест',
    email: 'test@example.com'
  },

  /** Полные корректные данные */
  complete: {
    ...generatePersonData(),
    ...generateProjectData()
  },

  /** Данные с потенциальными проблемами */
  problematic: {
    name: '   ', // Пробелы
    email: 'test@test', // Неполный email
    phone: '+7', // Неполный телефон
    website: 'http://', // Неполный URL
    notes: 'A'.repeat(1000) // Слишком длинный текст
  },

  /** Граничные случаи */
  edgeCases: {
    name: 'Ё'.repeat(50), // Максимальная длина с специальными символами
    email: 'test+tag@sub.domain.museum', // Сложный email
    phone: '+7 (800) 555-35-35 доб. 123', // Телефон с добавочным
    website: 'https://очень-длинное-название-сайта.рф/очень/длинный/путь?параметр=значение'
  }
};

export default {
  generatePersonData,
  generateProjectData,
  generateFormValidationData,
  generateTableData,
  generateTestErrors,
  simulateDelay,
  simulateRandomError,
  TestDataPresets
};